"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { differenceInMonths, format, startOfDay, startOfMonth, addDays, subDays, } from "date-fns";
import { useViewport, } from "@travel/shared/hooks";
import { Button } from "../../buttons/button/button";
import { DropDownV2 } from "../../dropdowns";
import { Input } from "../input/input";
import { Calendar } from "./calendar";
import styles from "./date-picker.module.css";
import "./date-picker.global.css";
var formatDate = function (date) {
    return date ? format(date, "MM/dd/yyyy") : "mm/dd/yyyy";
};
export var DatePicker = forwardRef(function DatePicker(props, ref) {
    var onChange = props.onChange, _a = props.mode, mode = _a === void 0 ? "single" : _a, _b = props.closeOnRangeSelect, closeOnRangeSelect = _b === void 0 ? false : _b, _c = props.min, min = _c === void 0 ? 0 : _c, _d = props.minRange, minRange = _d === void 0 ? 2 : _d, rest = __rest(props, ["onChange", "mode", "closeOnRangeSelect", "min", "minRange"]);
    var _e = __read(useState(false), 2), isFocused = _e[0], setIsFocused = _e[1];
    var _f = __read(useState(props.value), 2), range = _f[0], setRange = _f[1];
    var initialRangeRef = useRef(range);
    var inputRef = useRef();
    var value = useMemo(function () {
        return range
            ? "".concat(formatDate(range.from), " - ").concat(formatDate(range.to))
            : props.placeholder;
    }, [range, props.placeholder]);
    var initialIndex = differenceInMonths(startOfMonth((range === null || range === void 0 ? void 0 : range.from) || new Date()), startOfMonth(new Date()));
    var isSmall = useViewport().isSmall;
    useEffect(function () {
        if (isFocused) {
            initialRangeRef.current = range;
        }
        else {
            ensureValidDates();
        }
    }, [isFocused]);
    useEffect(function () {
        setRange(props.value);
    }, [props.value]);
    var disabledDates = [
        {
            from: startOfMonth(new Date()),
            to: subDays(new Date(), 1),
        },
    ];
    if ((range === null || range === void 0 ? void 0 : range.from) && !range.to) {
        disabledDates.push({
            from: addDays(range.from, 21),
            to: addDays(new Date(), 365 * 2),
        });
    }
    var disabled = useMemo(function () {
        return [[startOfMonth(new Date()), subDays(startOfDay(new Date()), 1)]];
    }, []);
    return (_jsx(_Fragment, { children: _jsx(DropDownV2, { open: isFocused, contentClassName: styles["panel"], contentDesiredHeight: 380, trigger: function (state, ref, isClone) { return (_jsx(Input, __assign({}, rest, { type: "button", value: value, forceFocused: state.isFocused, size: isSmall && isClone ? "lg" : "md", ref: ref }))); }, content: function () { return (_jsx("div", { className: styles["calendar"], children: _jsx(Calendar, { value: [(range === null || range === void 0 ? void 0 : range.from) || null, (range === null || range === void 0 ? void 0 : range.to) || null], controlClassName: styles["control"], controlsSize: "sm", hideControls: isSmall, direction: isSmall ? "vertical" : "horizontal", initialIndex: initialIndex, minRange: minRange, months: 12, maxRange: 21, disabled: disabled, onChange: function (_a) {
                        var _b = __read(_a, 2), from = _b[0], to = _b[1];
                        onChange === null || onChange === void 0 ? void 0 : onChange({
                            from: from,
                            to: to,
                        });
                    } }) })); }, footer: _jsx(Button, { theme: "secondary", inline: true, disabled: !(range === null || range === void 0 ? void 0 : range.to), inlineMobile: false, onClick: function () { return setIsFocused(false); }, children: "Done" }), unmountOnExit: true, onOpenChange: setIsFocused, ref: ref }) }));
    function handleClickClose() {
        var _a;
        ensureValidDates(initialRangeRef.current);
        (_a = props.onClickClose) === null || _a === void 0 ? void 0 : _a.call(props);
    }
    function ensureValidDates(dates) {
        if (dates === void 0) { dates = range; }
        var from = (dates === null || dates === void 0 ? void 0 : dates.from) || new Date();
        var to = (dates === null || dates === void 0 ? void 0 : dates.to) || addDays((dates === null || dates === void 0 ? void 0 : dates.from) || from, 1);
        setRange({ from: from, to: to });
        onChange === null || onChange === void 0 ? void 0 : onChange({
            from: from,
            to: to,
        });
    }
});
